import React from 'react';
import { Skeleton } from './Skeleton';

export interface StrategyMatchBarProps {
  /** Continuous score 0–100 representing the best-match strategy's match score. */
  matchScore: number;
  /** Best-match strategy name (truncated to 20 chars + ellipsis in UI). */
  strategyName: string;
  /** Number of strategies with match_score >= 50. */
  strategiesMatched: number;
  /** Total number of strategies evaluated. */
  strategiesEvaluated: number;
  /**
   * When true the component renders an animated shimmer skeleton.
   * Used for screener rows outside the pre-computed top-15 symbols
   * while lazy evaluation is in progress.
   */
  isLoading?: boolean;
}

/**
 * Returns the Tailwind fill-colour class for the progress bar
 * based on match_score thresholds defined in the architecture doc:
 *   >= 75 → emerald (strong match)
 *   >= 50 → amber  (standard match)
 *   <  50 → slate  (weak match)
 */
function barColour(score: number): string {
  if (score >= 75) return 'bg-emerald-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-slate-500';
}

/**
 * Truncates a strategy name to a maximum display length.
 * Keeps all characters up to maxLen, appending '…' if longer.
 */
function truncateName(name: string, maxLen = 20): string {
  if (name.length <= maxLen) return name;
  return name.slice(0, maxLen) + '…';
}

/**
 * StrategyMatchBar — compact strategy match evidence widget for the screener.
 *
 * Replaces the boolean "✓ Match" badge in ScreenerAssetRow.tsx.
 * Shows a horizontal progress bar, strategy name, and N/M count.
 *
 * Renders an animated shimmer skeleton when isLoading=true (beyond-top-15 rows).
 */
const StrategyMatchBar: React.FC<StrategyMatchBarProps> = ({
  matchScore,
  strategyName,
  strategiesMatched,
  strategiesEvaluated,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div
        className="w-full min-w-[80px] space-y-1.5"
        data-testid="strategy-match-bar-skeleton"
        aria-label="Strategy match loading"
      >
        {/* Skeleton bar track */}
        <Skeleton className="h-2 w-full rounded-full" />
        {/* Skeleton text row */}
        <div className="flex items-center justify-between gap-1">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-8" />
        </div>
      </div>
    );
  }

  const clampedScore = Math.max(0, Math.min(100, matchScore));
  const fillWidth = `${clampedScore}%`;
  const colour = barColour(clampedScore);
  const displayName = truncateName(strategyName);

  return (
    <div
      className="w-full min-w-[80px] space-y-1"
      data-testid="strategy-match-bar"
      aria-label={`Strategy match: ${clampedScore}% — ${strategyName}`}
    >
      {/* Progress bar track */}
      <div className="h-2 w-full bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${colour}`}
          style={{ width: fillWidth }}
          data-testid="strategy-match-bar-fill"
          aria-valuenow={clampedScore}
          aria-valuemin={0}
          aria-valuemax={100}
          role="progressbar"
        />
      </div>

      {/* Strategy name + N/M count */}
      <div className="flex items-center justify-between gap-1">
        <span
          className="text-[10px] text-slate-300 truncate leading-tight"
          title={strategyName}
          data-testid="strategy-match-bar-name"
        >
          {displayName}
        </span>
        <span
          className="text-[10px] text-slate-500 whitespace-nowrap flex-shrink-0"
          data-testid="strategy-match-bar-count"
        >
          {strategiesMatched}/{strategiesEvaluated}
        </span>
      </div>
    </div>
  );
};

export default StrategyMatchBar;
